/**
 * Partners — listado + crear / editar / eliminar.
 *
 * Alineado con Expogreens-main:
 *   - Tipos: customs_agency | supplier | airline
 *   - Campos: name, nit, address, phone, email (además del type)
 *   - Badge muestra el label ("Proveedor") no la key cruda ("supplier")
 *
 * Los tipos vienen de GET /api/partner-types como [{id,label}].
 */
import { useEffect, useMemo, useState } from 'react'
import { Building2, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { ModuleShell } from '@/components/ui/ModuleShell'
import { Segmented } from '@/components/ui/Segmented'
import { FilterBar } from '@/components/ui/FilterBar'
import { SearchButton } from '@/components/ui/SearchButton'
import { ChoiceButtons } from '@/components/ui/ChoiceButtons'
import { ScrollX } from '@/components/ui/ScrollX'
import { Drawer, DrawerField } from '@/components/ui/Drawer'
import { Button } from '@/components/ui/Button'
import { Modal, Field, Input } from '@/components/ui/Modal'
import { useApi } from '@/lib/useApi'
import { useMutation } from '@/lib/useMutation'
import { apiCall } from '@/lib/api'

const EMPTY = {
  name: '',
  type: 'supplier',
  nit: '',
  phone: '',
  email: '',
  address: '',
}

// Badge por tipo estilo handoff (#28): fondo suave + texto fuerte.
const TYPE_BADGE = {
  supplier: { bg: '#E3F0D5', fg: '#1E6E3A' },
  customs_agency: { bg: '#E7ECFB', fg: '#3B4FB0' },
  airline: { bg: '#F0E7FB', fg: '#6B3FB0' },
}
function typeBadgeStyle(type) {
  const c = TYPE_BADGE[type] || { bg: '#F0F3EE', fg: '#5C6B60' }
  return {
    display: 'inline-flex',
    alignItems: 'center',
    background: c.bg,
    color: c.fg,
    fontSize: 13,
    fontWeight: 700,
    padding: '5px 12px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
  }
}

/** Iniciales (máx. 2) para el chip de avatar de la fila. */
function initialsOf(name = '') {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

// Grid de columnas de la tabla #28.
const PARTNERS_GRID = '2.4fr 1fr 1.4fr 1.2fr'

export default function PartnersPage() {
  const { data, loading, error, refresh } = useApi('/partners')
  const types = useApi('/partner-types')
  const [editing, setEditing] = useState(null)
  const [selected, setSelected] = useState(null) // fila abierta en el panel
  const [filterType, setFilterType] = useState('all')
  const [query, setQuery] = useState('')
  // Tipo del partner en el modal (controlado, para usar botones en vez de select).
  const [partnerType, setPartnerType] = useState('supplier')
  useEffect(() => {
    if (editing) setPartnerType(editing.type || 'supplier')
  }, [editing])

  const save = useMutation(async (payload) => {
    if (payload.id) {
      return apiCall(`/partners/${payload.id}`, { method: 'PUT', body: payload })
    }
    return apiCall('/partners', { method: 'POST', body: payload })
  })

  const remove = useMutation(async (id) =>
    apiCall(`/partners/${id}`, { method: 'DELETE' }),
  )

  const typeOptions = Array.isArray(types.data) ? types.data : []
  const labelFor = (id) =>
    typeOptions.find((t) => t.id === id)?.label || id || '—'

  const filtered = useMemo(() => {
    const list = data || []
    const q = query.trim().toLowerCase()
    return list.filter((p) => {
      if (filterType !== 'all' && p.type !== filterType) return false
      if (!q) return true
      return `${p.name || ''} ${p.id || ''} ${p.nit || ''} ${p.phone || ''}`.toLowerCase().includes(q)
    })
  }, [data, filterType, query])

  const onSubmit = async (e) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const payload = Object.fromEntries(fd.entries())
    if (!payload.name?.trim()) {
      return toast.error('El nombre es obligatorio')
    }
    payload.type = partnerType
    if (editing?.id) payload.id = editing.id
    try {
      await save.mutate(payload)
      toast.success(editing?.id ? 'Partner actualizado' : 'Partner creado')
      setEditing(null)
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onDelete = async (id) => {
    if (!confirm('¿Eliminar este partner?')) return
    try {
      await remove.mutate(id)
      toast.success('Partner eliminado')
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  return (
    <div>
      <ModuleShell
        title="Partners"
        subtitle="Proveedores, agencias de aduanas y aerolíneas"
        icon={Building2}
        action={
          <div className="flex items-center gap-3">
            <SearchButton value={query} onChange={setQuery} placeholder="Buscar partner…" />
            <Button onClick={() => setEditing({ ...EMPTY })} className="!px-3 sm:!px-6">
              <Plus size={20} />
              <span className="hidden sm:inline">Nuevo partner</span>
            </Button>
          </div>
        }
        filters={
          <FilterBar left={
            <Segmented
              value={filterType}
              onChange={setFilterType}
              options={[{ value: 'all', label: 'Todos' }, ...typeOptions.map((t) => ({ value: t.id, label: t.label }))]}
            />
          } />
        }
      >
        {loading && <p className="text-expo-muted">Cargando…</p>}
        {error && <p className="text-red-600">Error: {error.message}</p>}
        {data && (
          <div style={{ background: '#fff', border: '1px solid #E6EFE3', borderRadius: 22, overflow: 'hidden' }}>
            {/* Cabecera #28: título + subtítulo a la izquierda, total a la derecha */}
            <div
              className="flex items-start justify-between gap-6 flex-wrap"
              style={{ padding: '28px 30px 22px', borderBottom: '1.5px solid #EAF0E6' }}
            >
              <div>
                <div className="font-display" style={{ fontSize: 28, fontWeight: 800, color: '#143A22', letterSpacing: '-0.01em', lineHeight: 1.05 }}>
                  Partners registrados
                </div>
                <div style={{ fontSize: 15, color: '#8B9B8F', fontWeight: 500, marginTop: 4 }}>
                  Proveedores, agencias de aduanas y aerolíneas
                </div>
              </div>
              <div style={{ textAlign: 'right', flex: 'none' }}>
                <div className="font-display" style={{ fontSize: 30, fontWeight: 800, color: '#033021', lineHeight: 1 }}>
                  {filtered.length}
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: '#9BA89B', marginTop: 5 }}>
                  PARTNERS · TOTAL
                </div>
              </div>
            </div>

            <ScrollX fadeColor="#fff">
            <div style={{ minWidth: 640 }}>
            {/* Etiquetas de columna */}
            <div
              className="grid uppercase"
              style={{ gridTemplateColumns: PARTNERS_GRID, gap: 16, padding: '14px 30px', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: '#A9B4AA' }}
            >
              <span>Partner</span>
              <span>Código</span>
              <span>Teléfono</span>
              <span>Tipo</span>
            </div>

            {filtered.length === 0 ? (
              <div style={{ padding: '48px 30px', textAlign: 'center', color: '#8B9B8F', fontSize: 14 }}>
                Sin partners registrados
              </div>
            ) : (
              filtered.map((r, i) => (
                <div
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="grid items-center cursor-pointer"
                  style={{ gridTemplateColumns: PARTNERS_GRID, gap: 16, padding: '18px 30px', borderTop: '1px solid #F2F5F0' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#FAFCF6')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div className="flex items-center min-w-0" style={{ gap: 15 }}>
                    <div
                      className="flex items-center justify-center flex-none"
                      style={{ width: 46, height: 46, borderRadius: 13, background: '#033021', color: '#C3DB5A', fontWeight: 800, fontSize: 16 }}
                    >
                      {initialsOf(r.name)}
                    </div>
                    <span className="truncate" style={{ fontSize: 19, fontWeight: 700, color: '#143A22' }}>
                      {r.name}
                    </span>
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#5C6B60' }}>{r.id}</span>
                  <span style={{ fontSize: 16, fontWeight: 600, color: '#5C6B60' }}>{r.phone || '—'}</span>
                  <span>
                    <span style={typeBadgeStyle(r.type)}>{labelFor(r.type)}</span>
                  </span>
                </div>
              ))
            )}
            </div>
            </ScrollX>
          </div>
        )}
      </ModuleShell>

      {/* Panel de detalle */}
      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        initials={(selected?.name || '').split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
        title={selected?.name}
        subtitle={selected?.id}
        badge={
          selected ? (
            <span className="bg-white/20 font-display font-semibold text-[11px] tracking-wide uppercase px-3 py-1 rounded-full">
              {labelFor(selected.type)}
            </span>
          ) : null
        }
        footer={
          selected ? (
            <>
              <Button
                variant="dark"
                className="flex-1"
                onClick={() => {
                  setEditing(selected)
                  setSelected(null)
                }}
              >
                <Pencil size={16} />
                <span>Editar</span>
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  await onDelete(selected.id)
                  setSelected(null)
                }}
              >
                <Trash2 size={16} />
                <span>Eliminar</span>
              </Button>
            </>
          ) : null
        }
      >
        {selected && (
          <div>
            <DrawerField label="ID" value={selected.id} />
            <DrawerField label="Tipo" value={labelFor(selected.type)} />
            <DrawerField label="NIT" value={selected.nit || '—'} />
            <DrawerField label="Teléfono" value={selected.phone || '—'} />
            <DrawerField label="Email" value={selected.email || '—'} />
            <DrawerField label="Dirección" value={selected.address || '—'} />
          </div>
        )}
      </Drawer>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Editar partner' : 'Nuevo partner'}
        subtitle="Proveedor, agencia de aduanas o aerolínea"
      >
        {editing && (
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label="Nombre" required>
              <Input name="name" defaultValue={editing.name} required />
            </Field>
            <Field label="Tipo" required>
              <ChoiceButtons
                value={partnerType}
                onChange={setPartnerType}
                options={typeOptions.map((t) => ({ value: t.id, label: t.label }))}
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="NIT">
                <Input name="nit" defaultValue={editing.nit} />
              </Field>
              <Field label="Teléfono">
                <Input name="phone" defaultValue={editing.phone} />
              </Field>
            </div>
            <Field label="Email">
              <Input type="email" name="email" defaultValue={editing.email} />
            </Field>
            <Field label="Dirección">
              <Input name="address" defaultValue={editing.address} />
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="secondary" onClick={() => setEditing(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={save.loading}>
                {save.loading ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
